import React, { useState, useEffect, useMemo } from "react";
import { useFormik, getIn } from "formik";
import * as Yup from "yup";
import * as Components from "../../../components";
import { FMDPStorage, mergeObjects } from "../../../shared/helper";
import { vehicleFormSchema } from "../validations";
import { FormContainer } from "../Shared";
import {
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useGetVMansQuery,
  useGetVManModelsQuery,
  useGetVManModelVariantsQuery,
  useGetVehicleQuery,
  useGetVModelsQuery,
  useGetDevicesQuery,
  useGetVehicleGroupsQuery,
  useGetUsersQuery,
  useGetUsersByRoleQuery,
  useGetCustomFieldsQuery,
} from "../services";
import { useDropDownSearch } from "../../../hooks";
import { getLocalizedString } from "../../../shared/translation";
import { vehiclePlaceholder } from "../../../assets";
import { ConfirmationModal } from "../Shared";
import { SvgIcon } from "../../Shared";
import { convertToEpoch } from "../../../shared/utility";
import {
  getVehicleGroupActionError,
  isVehicleGroupEditable,
} from "./groupAccess";

export const VehicleForm = ({ idToEdit = null, onClose }) => {
  const dropdownParams = { per_page: 1500 };
  const isSuperAdmin = FMDPStorage?.get("current-user")?.superadmin;
  const [initialValues, setInitialValues] = useState({
    name: "",
    vehicle_model_id: "",
    plate_number: "",
    engine_number: "",
    chasis_number: "",
    driver_id: "",
    vehicle_manufacturer_id: "",
    vehicle_manufacturer_model_id: "",
    vehicle_manufacturer_model_variant_id: "",
    device_ids: [],
    vehicle_group_ids: [],
    assigned_user_id: "",
    image: "",
    installation_date: null,
  });

  // Fetch custom fields for Vehicle asset type
  const { data: customFieldsData } = useGetCustomFieldsQuery({
    asset_type: "Vehicle",
    per_page: 100,
  });

  // Add custom fields to initial values when custom fields data is loaded
  useEffect(() => {
    if (customFieldsData?.data?.length && !idToEdit) {
      const customFieldInitialValues = {};
      customFieldsData.data.forEach((field) => {
        customFieldInitialValues[`custom_field_${field.name}`] = "";
      });
      setInitialValues((prev) => ({ ...prev, ...customFieldInitialValues }));
    }
  }, [customFieldsData, idToEdit]);

  // Create dynamic validation schema with custom fields (flattened)
  const validationSchema = useMemo(() => {
    if (!customFieldsData?.data?.length) {
      return vehicleFormSchema;
    }

    const customFieldValidations = {};
    customFieldsData.data.forEach((field) => {
      const displayLabel = field.label || (field.name.charAt(0).toUpperCase() + field.name.slice(1));
      const fieldKey = `custom_field_${field.name}`;
      let fieldValidation = Yup.string();

      // Add required validation
      if (field.required) {
        fieldValidation = fieldValidation.required(`${displayLabel} is required`);
      }

      // Add min/max length validation
      if (field.min) {
        fieldValidation = fieldValidation.min(parseInt(field.min), `Minimum ${field.min} characters`);
      }
      if (field.max) {
        fieldValidation = fieldValidation.max(parseInt(field.max), `Maximum ${field.max} characters`);
      }

      // Add regex validation
      if (field.regex_pattern) {
        const errorMessage = field.description
          ? `${displayLabel} does not match the required format. ${field.description}`
          : `${displayLabel} does not match the required format`;
        fieldValidation = fieldValidation.matches(new RegExp(field.regex_pattern), errorMessage);
      }

      customFieldValidations[fieldKey] = fieldValidation;
    });

    const schema = vehicleFormSchema.shape(customFieldValidations);
    return schema;
  }, [customFieldsData]);

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  // To handle confirmation message show for edit form in case of plate number change
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (value) => handleFormSubmit(value),
  });

  // Get the vehicle manufacturers list using Dropdown Search
  const {
    data: vehicleMans,
    setSearchKey: setVManSearch,
    isSuccess: isVehicleManSuccess,
  } = useDropDownSearch({
    useData: useGetVMansQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.vehicle_manufacturer_id,
  });

  // Get Vehicle Manufacturers Model list, if manufacture is selected using Dropdown Search
  const {
    data: vehicleManModels,
    setSearchKey: setVManModelSearch,
    isSuccess: isVehicleManModelSuccess,
  } = useDropDownSearch({
    useData: useGetVManModelsQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.vehicle_manufacturer_model_id,
    additionalParams: {
      per_page: 1000,
      vehicle_manufacturer_id: formik.values.vehicle_manufacturer_id,
    },
    additionalParamsToHook: { skip: !formik.values.vehicle_manufacturer_id },
  });

  /*  //Get Driver List to link with vehicle using Dropdown Search
  const {
    data: drivers = { data: [] },
    setSearchKey: setDriversSearch,
    isSuccess: isDriversSuccess,
  } = useDropDownSearch({
    useData: useGetDriversQuery,
    simpleSearchKey: "filter_full_name_cont",
    selectedIds: formik.values.driver_id,
  }); */

  //Get User List to link with vehicle using Dropdown Search
  const {
    data: users = { data: [] },
    setSearchKey: setUsersSearch,
    isSuccess: isUsersSuccess,
  } = useDropDownSearch({
    useData: useGetUsersQuery,
    simpleSearchKey: "filter_full_name_or_email_cont",
    selectedIds: formik.values.assigned_user_id,
  });

  //Get Driver List to link with vehicle using Dropdown Search
  const {
    data: drivers = { data: [] },
    setSearchKey: setDriversSearch,
    isSuccess: isDriversSuccess,
  } = useDropDownSearch({
    useData: useGetUsersByRoleQuery,
    simpleSearchKey: "filter_full_name_or_email_cont",
    selectedIds: formik.values.driver_id,
    additionalParams: { role: "driver" },
  });

  const { data: vehicleGroups, isSuccess: isVehicleGroupsSuccess } =
    useGetVehicleGroupsQuery(dropdownParams);

  // Set default vehicle group as System Default if its create form and system default group is present
  useEffect(() => {
    if (isVehicleGroupsSuccess && vehicleGroups?.data?.length > 0 && idToEdit === null) {
      vehicleGroups?.data.map((group) => {
        if (group.created_by === "System") {
          formik.setFieldValue("vehicle_group_ids", [group.id]);
        }
      });
    }
  }, [isVehicleGroupsSuccess]);

  // Get Vehicle Manufacturer Model Variant list, if manufacture model is selected
  const { data: vehicleManModVar, isSuccess: isVehicleManModVarSuccess } =
    useGetVManModelVariantsQuery(
      {
        per_page: 1000,
        vehicle_manufacturer_model_id: formik.values.vehicle_manufacturer_model_id,
        merge_model: true,
      },
      { skip: !formik.values.vehicle_manufacturer_model_id }
    );

  const deviceParams = {
    not_assigned: true,
  };
  if (idToEdit) {
    deviceParams.vehicle_id = idToEdit;
  }

  // Get the devices list using Dropdown Search
  const {
    data: devices,
    setSearchKey: setDevicesSearchKey,
    isSuccess: devicesSuccess,
  } = useDropDownSearch({
    useData: useGetDevicesQuery,
    simpleSearchKey: "name_or_device_id_or_filter_device_ph_with_country_code_cont",
    selectedIds: formik.values.device_ids,
    additionalParams: { ...deviceParams, per_page: 1500 },
    additionalParamsToHook: { ...dropdownParams },
  });

  const { data = {} } = useGetVehicleQuery({ id: idToEdit }, { skip: !idToEdit });

  const [createVehicle, { isLoading: isCreatingVehicle }] = useCreateVehicleMutation();
  const [updateVehicle, { isLoading: isUpdatingVehicle }] = useUpdateVehicleMutation();

  useEffect(() => {
    if (idToEdit && data) {
      // Transform custom_fields array to flattened custom_field_ structure
      const transformedData = { ...data };
      if (data.custom_fields && Array.isArray(data.custom_fields)) {
        data.custom_fields.forEach((field) => {
          transformedData[`custom_field_${field.label}`] = field.value || "";
        });
        delete transformedData.custom_fields;
        delete transformedData.custom_field_attributes;
      }
      setInitialValues((previousValues) => mergeObjects(previousValues, transformedData));
    }
  }, [data]);

  const handleFormSubmit = async (formData, plateChangeConfirmed = false) => {
    // Transform flattened custom fields back to nested structure (without mutating original)
    const custom_field_attributes = {};
    const transformedData = { ...formData };

    if (customFieldsData?.data?.length) {
      customFieldsData.data.forEach((field) => {
        const fieldKey = `custom_field_${field.name}`;
        if (transformedData[fieldKey] !== undefined) {
          custom_field_attributes[field.name] = transformedData[fieldKey];
          delete transformedData[fieldKey];
        }
      });
    }
    transformedData.custom_field_attributes = custom_field_attributes;

    // For edit form if plate number is changed then show confirmation message
    if (
      idToEdit &&
      formik?.values?.plate_number !== data?.plate_number &&
      !plateChangeConfirmed
    ) {
      setShowConfirmationModal(true);
      return;
    }

    const vehicleFormData = new FormData();
    Object.keys(transformedData).forEach((key) => {
      if (key === "image" && transformedData?.image == null) {
        vehicleFormData.append("remove_image", true);
      } else if (typeof transformedData[key] === "object" && key !== "image") {
        vehicleFormData.append(key, JSON.stringify(transformedData[key]));
      } else {
        vehicleFormData.append(key, transformedData[key]);
      }
    });

    try {
      if (idToEdit) {
        await updateVehicle({ id: idToEdit, formData: vehicleFormData }).unwrap();
      } else {
        await createVehicle({ formData: vehicleFormData }).unwrap();
      }

      onClose(false);
    } catch (error) {
      // Prefer field-level errors returned by the server (e.g. duplicate plate/engine/chassis
      // numbers) so they can be shown next to the corresponding field. Fall back to a generic
      // message only when the server did not return any recognizable field errors, and never
      // surface a raw JS error message (like a "reading 'map'" TypeError) to the user.
      const parsedError = normalizeVehicleErrors(extractFieldErrors(error));

      if (Object.keys(parsedError).length > 0) {
        // Mark the errored fields as touched so their inline error message is displayed.
        formik.setTouched(
          { ...formik.touched, ...mapKeysToTrue(parsedError) },
          false
        );
        formik.setErrors({ ...formik.errors, ...parsedError });
      } else {
        formik.setErrors({
          ...formik.errors,
          serverError: "Unable to save the vehicle. Please try again.",
        });
      }
    }
  };

  // Extract field-level validation errors from any shape the request layer might throw:
  // RTK's `{ status, data }`, a nested `{ data: { errors } }`, or a bare field map. Values may
  // be arrays (["has already been taken"]) or strings; both are normalized to a single string.
  const extractFieldErrors = (error) => {
    if (!error || typeof error !== "object") return {};

    // Server / gateway failures without useful field data.
    if (error.status === 500 || error.status === 404) return {};

    const candidate =
      (error.data && typeof error.data === "object" && (error.data.errors || error.data)) ||
      (typeof error.status === "undefined" && !error.message ? error : null);

    if (!candidate || typeof candidate !== "object") return {};

    return Object.keys(candidate).reduce((accumulator, key) => {
      const value = candidate[key];
      if (Array.isArray(value)) {
        accumulator[key] = value.join(", ");
      } else if (typeof value === "string") {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});
  };

  // Build a { key: true } map used to flag server-error fields as touched.
  const mapKeysToTrue = (errorObject) =>
    Object.keys(errorObject).reduce((accumulator, key) => {
      accumulator[key] = true;
      return accumulator;
    }, {});

  // Present clearer, user-facing messages for known duplicate-field errors while keeping the
  // original server message for anything else.
  const normalizeVehicleErrors = (errorObject) => {
    const duplicateMessages = {
      plate_number: getLocalizedString(
        "plate_number_already_exists",
        "Plate number already exists"
      ),
      engine_number: getLocalizedString(
        "engine_number_already_exists",
        "Engine number already exists"
      ),
      chasis_number: getLocalizedString(
        "chassis_number_already_exists",
        "Chassis number already exists"
      ),
    };

    return Object.keys(errorObject).reduce((accumulator, key) => {
      const message = errorObject[key];
      const isDuplicate =
        typeof message === "string" && message.toLowerCase().includes("has already been taken");
      accumulator[key] = isDuplicate && duplicateMessages[key] ? duplicateMessages[key] : message;
      return accumulator;
    }, {});
  };

  // Confirmation message for edit form in case of plate number change
  const getConfirmationMessage = () => {
    return (
      <div className="confirmation-message">
        <span>
          <SvgIcon name="alert" className="confirmation-message_icon" />
        </span>

        <p>
          <strong>Trips, events and other track points</strong> will not be available for this
          vehicle.
        </p>

        <p> Do you still want to update?</p>
      </div>
    );
  };

  // Function to check if the vehicle group is deletable
  const isVehicleGroupDeletable = (group) =>
    isVehicleGroupEditable(group, isSuperAdmin);

  // To show message when hovered on system group
  const handleVehicleGroupSelection = (group) =>
    getVehicleGroupActionError(group, isSuperAdmin);

  const handleSubscriptionDateChange = (date) => {
    const selectedTimestamp = convertToEpoch(date, "seconds");
    formik.setFieldValue("installation_date", selectedTimestamp);
  };

  const handleSubmitWithTouch = (e) => {
    // Mark all fields as touched before submit so that inline validation and server-side
    // errors (e.g. duplicate plate/engine/chassis numbers) are shown next to their field.
    // Merge with the existing touched state so fields the user already interacted with are
    // not reset to untouched.
    const allTouched = {
      ...formik.touched,
      name: true,
      vehicle_model_id: true,
      vehicle_manufacturer_id: true,
      vehicle_manufacturer_model_id: true,
      plate_number: true,
      engine_number: true,
      chasis_number: true,
    };

    // Add custom fields to touched
    if (customFieldsData?.data?.length) {
      customFieldsData.data.forEach((field) => {
        allTouched[`custom_field_${field.name}`] = true;
      });
    }

    formik.setTouched(allTouched);

    formik.handleSubmit(e);
  };

  return (
    <>
      <FormContainer
        resourceName={getLocalizedString("vehicle", "Vehicle")}
        closeForm={() => onClose(false)}
        handleFormSubmit={handleSubmitWithTouch}
        idToEdit={idToEdit}
        error={formik.errors}
        isLoading={isCreatingVehicle || isUpdatingVehicle}
      >
        <Components.QIPhotoUploader
          label={getLocalizedString("upload_image", "Upload Image")}
          src={formik.values.image}
          onChange={(files) => formik.setFieldValue("image", files?.length > 0 ? files[0] : null)}
          showJustImage
          base64={false}
          initalImageUrl={formik.values.image}
          placeHolderImg={vehiclePlaceholder}
          photoGuide={`
        <h4 className="font-semibold">${getLocalizedString(
          "image_upload_guide",
          "Image Upload guide"
        )} - </h4>
        <div className="ml-2">
          <p>${getLocalizedString("max_size", "Max size")} 100kb</p>
          <p>${getLocalizedString("file_format", "File format")} .jpg/ .jpeg/ .png/ .gif</p>
          <p>${getLocalizedString(
            "preferred_image_aspect_ratio",
            "Preferred image aspect ratio"
          )} 1:1</p>
        </div>
      `}
          error={formik.touched.image && formik.errors.image}
        />

        <Components.QICustomSelect
          label={getLocalizedString("manufacturer", "Manufacturer")}
          value={formik.values.vehicle_manufacturer_id}
          onChange={(value) => {
            formik.setValues({
              ...formik.values,
              vehicle_manufacturer_id: value,
              vehicle_manufacturer_model_id: "",
              vehicle_model_id: "",
            });
            setVManSearch("");
          }}
          onSearch={setVManSearch}
          error={formik.touched.vehicle_manufacturer_id && formik.errors.vehicle_manufacturer_id}
          labelClassName="manufacturer"
        >
          {isVehicleManSuccess &&
            vehicleMans.data.map((vMan) => (
              <li key={vMan.id} value={vMan.id}>
                {vMan.name}
              </li>
            ))}
        </Components.QICustomSelect>
        <Components.QICustomSelect
          label={getLocalizedString("model", "Model")}
          value={formik.values.vehicle_manufacturer_model_id}
          onChange={(value) => {
            formik.setValues({
              ...formik.values,
              vehicle_manufacturer_model_id: value,
              vehicle_model_id: "",
            });
            setVManModelSearch("");
          }}
          onSearch={setVManModelSearch}
          error={
            formik.touched.vehicle_manufacturer_model_id &&
            formik.errors.vehicle_manufacturer_model_id
          }
          disabled={!formik.values.vehicle_manufacturer_id}
          labelClassName="model"
        >
          {isVehicleManModelSuccess &&
            vehicleManModels.data.map((vMan) => (
              <li key={vMan.id} value={vMan.id}>
                {vMan.name}
              </li>
            ))}
        </Components.QICustomSelect>

        <Components.QICustomSelect
          label={getLocalizedString("specification", "Specification")}
          value={formik.values.vehicle_model_id}
          onChange={(value) => {
            formik.setFieldValue("vehicle_model_id", value);
          }}
          error={formik.touched.vehicle_model_id && formik.errors.vehicle_model_id}
          disabled={!formik.values.vehicle_manufacturer_model_id}
          labelClassName="specifications"
        >
          {isVehicleManModVarSuccess &&
            vehicleManModVar.data.map((vManVar) => (
              <li key={vManVar.vehicle_model_id} value={vManVar.vehicle_model_id}>
                Name: {vManVar.name}, Engine Capacity: {vManVar.engine_capacity}, Capacity:
                {vManVar.capacity}, Year: {vManVar.year}
              </li>
            ))}
        </Components.QICustomSelect>
        <Components.QIInput
          label={getLocalizedString("name", "Name")}
          {...formik.getFieldProps("name")}
          error={formik.touched.name && formik.errors.name}
        />
        <Components.QIInput
          label={getLocalizedString("plate_no", "Plate No.")}
          {...formik.getFieldProps("plate_number")}
          error={formik.touched.plate_number && formik.errors.plate_number}
        />

        {showConfirmationModal && (
          <ConfirmationModal
            onCancel={() => {
              setShowConfirmationModal(false);
            }}
            onConfirm={() => {
              setShowConfirmationModal(false);
              handleFormSubmit(formik.values, true);
            }}
            show={showConfirmationModal}
            headerText="Confirmation Alert!"
          >
            {getConfirmationMessage()}
          </ConfirmationModal>
        )}

        <Components.QIInput
          label={getLocalizedString("engine_number", "Engine Number")}
          placeholder={getLocalizedString("engine_number", "Engine Number")}
          {...formik.getFieldProps("engine_number")}
          error={formik.touched.engine_number && formik.errors.engine_number}
        />
        <Components.QIInput
          label={getLocalizedString("chasis_number", "Chassis Number")}
          placeholder={getLocalizedString("chasis_number", "Chassis Number")}
          {...formik.getFieldProps("chasis_number")}
          error={formik.touched.chasis_number && formik.errors.chasis_number}
        />
        <Components.QIMultiSelectDropDown
          label={getLocalizedString("groups", "Groups")}
          selected={formik.values.vehicle_group_ids}
          onChange={(value) => formik.setFieldValue("vehicle_group_ids", value)}
          data={vehicleGroups?.data || []}
          className="narrow-multiselect-dropdown"
          isDeletable={isVehicleGroupDeletable}
          actionError={handleVehicleGroupSelection}
        />
        <Components.QIMultiSelectDropDown
          label={getLocalizedString("devices", "Devices")}
          labelKey="device_id"
          selected={formik.values.device_ids}
          data={devices.data}
          onChange={(data) => {
            formik.setFieldValue("device_ids", data);
            setDevicesSearchKey("");
          }}
          onSearch={(value) => setDevicesSearchKey(value)}
          error={formik.touched.device_ids && formik.errors.device_ids}
        />

        <Components.QICalendar
          label={getLocalizedString("Installation_Date", "Installation Date")}
          className="subscription-date-and-days_item1"
          value={formik?.values?.installation_date * 1000}
          onChange={handleSubscriptionDateChange}
          showTimeSelect={true}
          timeIntervals={15}
          dateFormat="dd/MM/yyyy h:mm aa"
          maxDate={new Date()}
        />

        <Components.QICustomSelect
          label={getLocalizedString("user", "User")}
          value={formik.values.assigned_user_id}
          onChange={(value) => {
            formik.setFieldValue("assigned_user_id", value);
            setUsersSearch("");
          }}
          onSearch={setUsersSearch}
          onSearchPlaceHolder={getLocalizedString("search_by_name_email", "Search by Name, Email")}
          error={formik.touched.assigned_user_id && formik.errors.assigned_user_id}
          labelClassName="user"
        >
          <li value="">{getLocalizedString("select", "Select")}</li>
          {users?.data.map((user) => (
            <li value={user.id} key={user.id} className="qi-custom-select_dropdown_list_item">
              {user.first_name + " " + user.last_name + " (" + user.email + ")"}
            </li>
          ))}
        </Components.QICustomSelect>
        <Components.QICustomSelect
          label={getLocalizedString("driver", "Driver")}
          value={formik.values.driver_id}
          onChange={(value) => {
            setSelectedDriver(value);
            setShowDriverModal(true);
            setDriversSearch("");
          }}
          onSearch={setDriversSearch}
          error={formik.touched.driver_id && formik.errors.driver_id}
          labelClassName="user"
          className="temp-class"
        >
          <li value="">
            {getLocalizedString("select_a_user_as_driver", "Select a user as driver")}
          </li>
          {drivers?.data.map((user) => (
            <li value={user.id} key={user.id} className="qi-custom-select_dropdown_list_item">
              {user.first_name + " " + user.last_name + " (" + user.email + ")"}
            </li>
          ))}
        </Components.QICustomSelect>

        {/* Dynamic Custom Fields */}
        {customFieldsData?.data?.length > 0 && (
          <div className="custom-fields-section">
            <h4 className="form-section-title font-bold mb-4">{getLocalizedString("custom_fields", "Custom Fields")}</h4>

            {customFieldsData.data.map((field) => {
              const displayLabel = field.label || (field.name.charAt(0).toUpperCase() + field.name.slice(1));
              const fieldKey = `custom_field_${field.name}`;

              return (
                <Components.QIInput
                  key={field.name}
                  label={displayLabel}
                  placeholder={field.placeholder || `Enter ${displayLabel}`}
                  {...formik.getFieldProps(fieldKey)}
                  error={formik.touched[fieldKey] && formik.errors[fieldKey]}
                />
              );
            })}
          </div>
        )}
      </FormContainer>
      <Components.QIModal className="confirm" show={showDriverModal} size="small">
        <Components.QIModalBody>
          <h2 className="font-bold mb-5">Confirmation Alert</h2>
          <p className="">
            You are about to associate a driver, this might reset previous association.
          </p>
        </Components.QIModalBody>
        <Components.QIModalFooter>
          <Components.QIButton
            onClick={() => setShowDriverModal(false)}
            className="qi-btn secondary md"
            variant="secondary"
            size="sm"
          >
            No
          </Components.QIButton>
          <Components.QIButton
            onClick={() => {
              formik.setFieldValue("driver_id", selectedDriver);
              setShowDriverModal(false);
            }}
            size="sm"
            variant="danger"
            className="qi-btn primary md"
          >
            Yes
          </Components.QIButton>
        </Components.QIModalFooter>
      </Components.QIModal>
    </>
  );
};
